import { Component } from '@theme/component';
import { QuickAddComponent } from '@theme/quick-add';
import { CartUpdateEvent } from '@theme/events';
import { isClickedOutside, isMobileBreakpoint, mediaQueryLarge } from '@theme/utilities';

/**
 * A custom element that manages a dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLButtonElement} trigger - The button element.
 * @property {HTMLAnchorElement} productLink - The product link element.
 *
 * @extends Component<Refs>
 */

export class ProductHotspotComponent extends Component {
  requiredRefs = ['trigger', 'dialog'];
  /** @type {(() => void) | null} */
  #pointerenterHandler = null;
  timer = /** @type {number | null} */ (null);

  connectedCallback() {
    super.connectedCallback();

    // Set up initial event listeners based on current breakpoint
    this.#handleBreakpointChange();

    // Listen for breakpoint changes
    mediaQueryLarge.addEventListener('change', this.#handleBreakpointChange);

    // Intercept inline add-to-cart form submissions
    const atcForm = this.querySelector('.hotspot-dialog__atc-form');
    if (atcForm) {
      atcForm.addEventListener('submit', this.#handleAtcSubmit);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    // Clean up listeners
    this.#removeDesktopListeners();
    mediaQueryLarge.removeEventListener('change', this.#handleBreakpointChange);

    const atcForm = this.querySelector('.hotspot-dialog__atc-form');
    if (atcForm) {
      atcForm.removeEventListener('submit', this.#handleAtcSubmit);
    }
  }

  /**
   * Handle inline add-to-cart form submission via AJAX.
   * @param {SubmitEvent} e
   * @returns {Promise<void>}
   */
  #handleAtcSubmit = async (e) => {
    e.preventDefault();
    const form = /** @type {HTMLFormElement} */ (e.target);
    const button = form.querySelector('.hotspot-dialog__atc-button');
    if (button) {
      button.disabled = true;
      button.classList.add('hotspot-dialog__atc-button--loading');
    }

    try {
      const formData = new FormData(form);
      const body = JSON.stringify({
        items: [
          {
            id: Number(formData.get('id')),
            quantity: Number(formData.get('quantity') || 1),
          },
        ],
      });

      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body,
      });

      if (!res.ok) throw new Error('Add to cart failed');

      // Fetch updated cart and dispatch CartUpdateEvent so cart drawer / count refreshes
      const cartRes = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      const cart = await cartRes.json();
      document.dispatchEvent(new CartUpdateEvent(cart, this.dataset.id || 'hotspot'));

      // Brief visual feedback then close dialog
      if (button) {
        button.classList.remove('hotspot-dialog__atc-button--loading');
        button.classList.add('hotspot-dialog__atc-button--success');
        await new Promise((r) => setTimeout(r, 600));
      }
      this.closeDialog();
    } catch {
      // Silently fail — user can retry
    } finally {
      if (button) {
        button.disabled = false;
        button.classList.remove('hotspot-dialog__atc-button--loading');
        button.classList.remove('hotspot-dialog__atc-button--success');
      }
    }
  };

  /**
   * Open the quick-add modal, or directly add to cart for single-variant products
   * @returns {void}
   */
  #openQuickAddModal() {
    const quickAddComponent = /** @type {QuickAddComponent | null} */ (this.querySelector('quick-add-component'));

    if (!quickAddComponent) return;

    if (quickAddComponent.dataset.quickAddButton === 'add') {
      const form = quickAddComponent.querySelector('form');
      if (form) {
        form.requestSubmit();
        return;
      }
    }

    quickAddComponent.handleClick(new MouseEvent('click', { bubbles: true, cancelable: true }));
  }

  /**
   * Set up desktop event listeners (hover)
   * @returns {void}
   */
  #setupDesktopListeners() {
    const { trigger, dialog } = this.refs;

    /** @type {() => void} */
    const pointerenterHandler = () => {
      // Cancel any pending close when re-entering
      if (this.#closeTimer) {
        clearTimeout(this.#closeTimer);
        this.#closeTimer = null;
      }

      if (dialog.open) return;

      this.timer = setTimeout(() => {
        this.showDialog();
      }, 120);
      // Add pointerleave listener when entering trigger
      trigger.addEventListener('pointerleave', this.#handlePointerLeave);
    };

    this.#pointerenterHandler = pointerenterHandler;
    trigger.addEventListener('pointerenter', pointerenterHandler);
  }

  /**
   * Remove desktop event listeners from trigger
   * @returns {void}
   */
  #removeDesktopListeners() {
    const { trigger } = this.refs;

    if (this.#pointerenterHandler) {
      trigger.removeEventListener('pointerenter', this.#pointerenterHandler);
      trigger.removeEventListener('pointerleave', this.#handlePointerLeave);
      this.#pointerenterHandler = null;
    }

    // Clear any pending timer
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
  }

  /**
   * Handle breakpoint changes
   * @returns {void}
   */
  #handleBreakpointChange = () => {
    // Remove existing listeners
    this.#removeDesktopListeners();

    // Set up desktop hover listeners only (mobile uses on:click in template)
    if (!isMobileBreakpoint()) {
      this.#setupDesktopListeners();
    }
  };

  /**
   * Close other open hotspot dialogs in the same hotspots section (one popover at a time on touch).
   * @returns {void}
   */
  #closeSiblingHotspotDialogs() {
    const container = this.closest('.hotspots-container');
    if (!(container instanceof HTMLElement)) return;
    for (const el of container.querySelectorAll('product-hotspot-component')) {
      if (el === this || !(el instanceof ProductHotspotComponent)) continue;
      const otherDialog = el.querySelector('dialog.hotspot-dialog');
      if (!(otherDialog instanceof HTMLDialogElement) || !otherDialog.open) continue;
      void el.closeDialog();
    }
  }

  /**
   * Calculate the placement of the dialog.
   * @returns {Promise<void> | undefined}
   */
  #calculateDialogPlacement() {
    const { trigger, dialog } = this.refs;

    const hotspotsContainer = this.parentElement;

    if (!hotspotsContainer) {
      return;
    }

    // Spacing constants
    const BUTTON_GAP = 10; // Gap between button and dialog
    const CONTAINER_GAP = 10; // Gap from container edges

    // Get container bounds
    const containerRect = hotspotsContainer?.getBoundingClientRect();

    // Get button dimensions
    const triggerRect = trigger.getBoundingClientRect();

    // To get dialog dimensions, we need to temporarily show it invisibly
    // Show dialog invisibly to measure it
    dialog.style.visibility = 'hidden';
    dialog.style.display = 'block';
    dialog.style.transform = 'none';
    dialog.removeAttribute('data-placement');

    const { width: dialogWidth, height: dialogHeight } = dialog.getBoundingClientRect();

    // Reset dialog state
    dialog.style.removeProperty('display');
    dialog.style.removeProperty('visibility');
    dialog.style.removeProperty('transform');
    // Calculate button position relative to container (used for vertical placement)
    const buttonLeft = triggerRect.left - containerRect.left;
    const buttonRight = triggerRect.right - containerRect.left;
    const buttonTop = triggerRect.top - containerRect.top;
    const buttonBottom = triggerRect.bottom - containerRect.top;

    // Horizontal: use viewport, not only the hotspots box — page-width sections leave
    // "room" inside the container while the popover still clips at the screen edge.
    const vw = window.visualViewport?.width ?? window.innerWidth;
    const vEdge = CONTAINER_GAP;
    const spaceRightViewport = vw - triggerRect.right - vEdge;
    const spaceLeftViewport = triggerRect.left - vEdge;
    const minHSpace = dialogWidth + BUTTON_GAP;

    let x = 'right';
    if (spaceRightViewport >= minHSpace) {
      x = 'right';
    } else if (spaceLeftViewport >= minHSpace) {
      x = 'left';
    } else if (spaceLeftViewport > spaceRightViewport) {
      x = 'left';
    } else {
      x = 'right';
    }

    // Determine vertical placement (relative to hotspots container)
    let y = 'bottom';
    let verticalOffset = 0;

    let dialogStartY = buttonTop;
    let dialogEndY = buttonTop + dialogHeight;

    if (dialogEndY > containerRect.height - CONTAINER_GAP) {
      dialogStartY = buttonBottom - dialogHeight;
      dialogEndY = buttonBottom;
      y = 'top';

      if (dialogStartY < CONTAINER_GAP) {
        verticalOffset = CONTAINER_GAP - dialogStartY;
      } else if (dialogEndY > containerRect.height - CONTAINER_GAP) {
        verticalOffset = -(dialogEndY - (containerRect.height - CONTAINER_GAP));
      }
    } else if (dialogStartY < CONTAINER_GAP) {
      verticalOffset = CONTAINER_GAP - dialogStartY;
      y = 'bottom';
    }

    // Set placement data attribute
    dialog.dataset.placement = `${x},${y}`;

    // Apply vertical offset if needed to keep dialog in bounds
    if (verticalOffset !== 0) {
      dialog.style.setProperty('--dialog-vertical-offset', `${verticalOffset}px`);
    } else {
      dialog.style.removeProperty('--dialog-vertical-offset');
    }

    // Return a promise that resolves after a few ticks to ensure styles are applied
    return new Promise((resolve) => setTimeout(resolve, 100));
  }

  /**
   * Handle pointer leave.
   * @param {PointerEvent} e - The event.
   * @returns {void}
   */
  /** @type {number | null} */
  #closeTimer = null;

  #handlePointerLeave = (e) => {
    const { dialog, trigger } = this.refs;

    // Clear open timer if leaving trigger before dialog opens
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }

    if (!dialog.open) return;

    const isGoingToDialog =
      e.relatedTarget === dialog ||
      (e.relatedTarget instanceof Element && e.relatedTarget.closest('dialog') === dialog);
    const isGoingToTrigger = e.relatedTarget === trigger;
    const isGoingToSelf = e.relatedTarget === this || (e.relatedTarget instanceof Element && this.contains(e.relatedTarget));

    if (isGoingToDialog || isGoingToTrigger || isGoingToSelf) return;

    // Delay close to allow mouse to traverse the gap between trigger and dialog
    if (this.#closeTimer) clearTimeout(this.#closeTimer);
    this.#closeTimer = setTimeout(() => {
      this.#closeTimer = null;
      if (!dialog.open) return;
      // Check if pointer is now over the component or dialog
      const hovered = this.matches(':hover') || dialog.matches(':hover');
      if (!hovered) {
        this.closeDialog();
      }
    }, 150);
  };

  /**
   * Cancel pending close when pointer enters dialog.
   * @returns {void}
   */
  #handleDialogPointerEnter = () => {
    if (this.#closeTimer) {
      clearTimeout(this.#closeTimer);
      this.#closeTimer = null;
    }
  };

  /**
   * Get the product link for the hotspot product.
   * @returns {HTMLAnchorElement | null} The product link or null.
   */
  getHotspotProductLink() {
    return this.refs.productLink || null;
  }

  /**
   * Handle hotspot click - on mobile/touch tap shows panel, on desktop hover shows panel
   * @param {MouseEvent} e - The click event
   * @returns {void}
   */
  handleHotspotClick = (e) => {
    const isTouchDevice = matchMedia('(hover: none)').matches;

    if (isMobileBreakpoint() || isTouchDevice) {
      e.preventDefault();
      e.stopPropagation();
    }

    this.showDialog();
  };

  showDialog = async () => {
    // Cancel any pending close
    if (this.#closeTimer) {
      clearTimeout(this.#closeTimer);
      this.#closeTimer = null;
    }
    this.#closeSiblingHotspotDialogs();
    const { dialog } = this.refs;
    await this.#calculateDialogPlacement();
    dialog.dataset.showing = 'true';
    dialog.show();
    document.body.addEventListener('click', this.lightDismissMouse);
    document.body.addEventListener('keydown', this.lightDismissKeyboard);
    document.body.addEventListener('keyup', this.lightDismissKeyboard);
    // Add pointerleave and pointerenter listeners to dialog when it opens
    dialog.addEventListener('pointerleave', this.#handlePointerLeave);
    dialog.addEventListener('pointerenter', this.#handleDialogPointerEnter);
  };

  /**
   * Close the dialog.
   * @returns {Promise<void>}
   */
  closeDialog = async () => {
    const { dialog, trigger } = this.refs;
    dialog.dataset.closing = 'true';
    dialog.close();
    document.body.removeEventListener('click', this.lightDismissMouse);
    document.body.removeEventListener('keydown', this.lightDismissKeyboard);
    document.body.removeEventListener('keyup', this.lightDismissKeyboard);
    // Remove pointer listeners when closing
    dialog.removeEventListener('pointerleave', this.#handlePointerLeave);
    dialog.removeEventListener('pointerenter', this.#handleDialogPointerEnter);
    trigger.removeEventListener('pointerleave', this.#handlePointerLeave);
    if (this.#closeTimer) {
      clearTimeout(this.#closeTimer);
      this.#closeTimer = null;
    }
    // we need to use a data-attribute to keep transition-behavior working only when open
    const animations = dialog.getAnimations({ subtree: true });
    await Promise.allSettled(animations.map((a) => a.finished));
    if (!dialog.open) {
      delete dialog.dataset.showing;
      delete dialog.dataset.closing;
      delete dialog.dataset.placement;
    }
  };

  /**
   * Light dismiss the dialog.
   * @param {MouseEvent} event - The event.
   * @returns {void}
   */
  lightDismissMouse = (event) => {
    const { dialog } = this.refs;
    if (isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  };

  /**
   * Light dismiss the dialog.
   * @param {KeyboardEvent} event - The event.
   * @returns {void}
   */
  lightDismissKeyboard = (event) => {
    const { dialog } = this.refs;
    if (
      (event.type === 'keydown' && event.key === 'Escape') ||
      (event.type === 'keyup' && !dialog.matches(':is(:focus, :focus-visible, :focus-within)'))
    ) {
      this.closeDialog();
    }
  };
}

// Register custom element
customElements.define('product-hotspot-component', ProductHotspotComponent);
